import { useState } from 'react';
import { Link } from 'react-router-dom';
import { User, Building2, Briefcase } from 'lucide-react';
import MainTabsNav from '../components/MainTabsNav';

interface FoundAccount {
  maskedEmail: string;
  memberType: string;
}

const memberTypeLabel: Record<string, string> = {
  user: '일반 회원',
  provider: '교육 기관',
  employer: '기업 회원',
};

const memberTypeIcon: Record<string, typeof User> = {
  user: User,
  provider: Building2,
  employer: Briefcase,
};

export default function FindIdPage() {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<FoundAccount[] | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name || !phone) {
      setError('이름과 휴대폰 번호를 입력해주세요.');
      return;
    }

    setSearching(true);
    try {
      const response = await fetch('/api/v1/auth/find-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, phone }),
      });
      const data = await response.json();
      if (response.ok) {
        setResult(data.accounts || []);
      } else {
        setError(data.error || '아이디 조회에 실패했습니다.');
      }
    } catch (err) {
      console.error('Find email error:', err);
      setError('아이디 조회 중 오류가 발생했습니다.');
    } finally {
      setSearching(false);
    }
  };

  return (
    <>
      <MainTabsNav />
      <div className="flex items-center justify-center py-8 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <img src="/logo/stampfit-logo.png" alt="StampFit" className="w-[180px] mx-auto" />
          </Link>
        </div>

        <div className="bg-white rounded-2xl shadow-soft p-8">
          {result ? (
            <div>
              <h1 className="text-xl font-bold text-gray-900 text-center mb-6">아이디 조회 결과</h1>
              {result.length === 0 ? (
                <p className="text-gray-500 text-sm text-center mb-6">
                  입력하신 정보와 일치하는 가입 내역을 찾을 수 없습니다.
                </p>
              ) : (
                <div className="space-y-3 mb-6">
                  {result.map((account, idx) => {
                    const Icon = memberTypeIcon[account.memberType] || User;
                    return (
                      <div
                        key={idx}
                        className="flex items-center gap-3 p-4 border border-gray-200 rounded-xl"
                      >
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                          <Icon className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">
                            {memberTypeLabel[account.memberType] || '일반 회원'}
                          </p>
                          <p className="font-semibold text-gray-900">{account.maskedEmail}</p>
                        </div>
                      </div>
                    );
                  })}
                  <p className="text-xs text-gray-400 text-center pt-1">
                    보안을 위해 이메일 일부만 표시됩니다.
                  </p>
                </div>
              )}
              <div className="flex gap-3">
                <Link
                  to="/login"
                  className="flex-1 text-center font-semibold px-6 py-3 rounded-lg bg-primary hover:bg-primary-600 text-white transition-colors"
                >
                  로그인하러 가기
                </Link>
                <Link
                  to="/forgot-password"
                  className="flex-1 text-center font-semibold px-6 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  비밀번호 찾기
                </Link>
              </div>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">아이디 찾기</h1>
              <p className="text-gray-500 text-center text-sm mb-8">
                가입 시 등록한 이름과 휴대폰 번호를 입력하시면
                <br />
                아이디(이메일)를 확인할 수 있습니다.
              </p>

              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm text-center">{error}</p>
                </div>
              )}

              <form className="space-y-4" onSubmit={handleSubmit}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">이름</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="가입 시 등록한 이름"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">휴대폰 번호</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="010-1234-5678"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none transition-all"
                  />
                </div>

                <button
                  type="submit"
                  disabled={searching}
                  className={`w-full font-semibold px-6 py-3 rounded-lg transition-colors ${
                    searching
                      ? 'bg-gray-400 cursor-not-allowed text-white'
                      : 'bg-primary hover:bg-primary-600 text-white'
                  }`}
                >
                  {searching ? '조회 중...' : '아이디 찾기'}
                </button>
              </form>

              <div className="mt-6 flex items-center justify-center gap-3 text-sm text-gray-500">
                <Link to="/forgot-password" className="hover:text-primary hover:underline">
                  비밀번호 찾기
                </Link>
                <span className="text-gray-300">|</span>
                <Link to="/login" className="hover:text-primary hover:underline">
                  로그인으로 돌아가기
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
      </div>
    </>
  );
}
